import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  App,
  AutoComplete,
  Button,
  Input,
  Popover,
  Radio,
  Select,
  Space,
  Typography,
  theme as antdTheme,
} from 'antd';
import { space, useConfirmAction } from '@viq-admin/ui';
import { CRM_COPY } from './crm/copy';
import { runBulk, SERVER_WRITE_BURST } from './bulkRunner';
import { buildTagCatalog } from './tagCatalog';
import type { BatchBulkApi, BatchTagsApi, CustomTagDefinition } from './types';

const leadWord = (n: number): string =>
  n === 1 ? CRM_COPY.bulk.leadSingular : CRM_COPY.bulk.leadPlural;

interface Props {
  bulkApi: BatchBulkApi;
  /** Supplies the custom tag definitions only — the writes go through `bulkApi`. */
  tagsApi: BatchTagsApi;
  /** The leads table's active tab. A removed lead accepts Restore and nothing else. */
  tab: 'active' | 'deleted';
  selectedIds: string[];
  onClearSelection: () => void;
  /** Clears the selection AND re-fetches the table (BatchScreen's reloadToken). */
  onCompleted: () => void;
  /**
   * Names to offer in the assign panel — the connected CRM's owner roster
   * (spec 2026-10-02 §5.1). Free text still applies. Absent/empty → plain input.
   */
  suggestions?: string[];
  /** Opens the move dialog. Wired only where moving is allowed (portal, flag on). */
  onMove?: () => void;
}

/**
 * Actions for the leads table's row selection (customer portal).
 *
 * Rendered only with a non-empty selection, directly above the table card so
 * it fights neither the sticky table header nor the sticky footer. Every
 * action is a fan-out of per-lead writes through `runBulk` — the customer
 * plane has no bulk endpoint — so partial success is the normal case and the
 * summary toast always names how many leads actually changed.
 *
 * Portal-safe by construction: everything arrives through `bulkApi`, which
 * the console never wires.
 */
export function BulkActionsBar({
  bulkApi,
  tagsApi,
  tab,
  selectedIds,
  onClearSelection,
  onCompleted,
  suggestions,
  onMove,
}: Props) {
  const { token } = antdTheme.useToken();
  const { message } = App.useApp();
  const confirm = useConfirmAction();

  const [busy, setBusy] = useState(false);
  /**
   * How far the in-flight run has got. A big selection is deliberately paced
   * to the server's one-write-a-second budget (`runBulk`), so it can take a
   * couple of minutes — without a count, a bar that just sits there disabled
   * reads as a hang.
   */
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [openPanel, setOpenPanel] = useState<'tag' | 'assign' | null>(null);
  const [tagMode, setTagMode] = useState<'add' | 'remove'>('add');
  const [tagKey, setTagKey] = useState<string | null>(null);
  const [assignDraft, setAssignDraft] = useState('');

  // Same key + staleTime as BatchTable's own definitions query, so the two
  // share one cache entry rather than firing a second request.
  const tagDefsQuery = useQuery<CustomTagDefinition[]>({
    queryKey: ['batchCustomTagDefinitions'],
    queryFn: async () => (await tagsApi.fetchTagDefinitions()).definitions,
    staleTime: 60_000,
    enabled: tab === 'active',
  });
  const catalog = useMemo(
    () => buildTagCatalog(tagDefsQuery.data ?? []),
    [tagDefsQuery.data],
  );

  async function run(verb: string, op: (id: string) => Promise<void>): Promise<void> {
    const ids = selectedIds;
    setBusy(true);
    setProgress({ done: 0, total: ids.length });
    setOpenPanel(null);
    try {
      const { succeeded, failed } = await runBulk(ids, op, {
        onProgress: (done, total) => setProgress({ done, total }),
      });
      if (succeeded.length === 0) {
        message.error(CRM_COPY.bulk.allFailed);
      } else {
        const summary = `${verb} ${succeeded.length} ${CRM_COPY.bulk.resultOf} ${ids.length} ${leadWord(ids.length)}`;
        if (failed.length > 0) {
          message.warning(
            `${summary}. ${failed.length} ${CRM_COPY.bulk.resultFailedSuffix}.`,
          );
        } else {
          message.success(summary);
        }
      }
      if (failed.length > 0) {
        // eslint-disable-next-line no-console
        console.error('bulk action failures', failed);
      }
    } finally {
      setBusy(false);
      setProgress(null);
      onCompleted();
    }
  }

  function applyTag() {
    const picked = catalog.find((o) => o.key === tagKey);
    if (!picked) return;
    const { category, value } = picked;
    const adding = tagMode === 'add';
    void run(adding ? CRM_COPY.bulk.taggedVerb : CRM_COPY.bulk.untaggedVerb, (id) =>
      adding
        ? bulkApi.addTag(id, { category, value })
        : bulkApi.removeTag(id, category, value),
    );
  }

  function applyAssignment() {
    const trimmed = assignDraft.trim();
    void run(CRM_COPY.bulk.assignedVerb, (id) =>
      bulkApi.setAssignment(id, trimmed === '' ? null : trimmed),
    );
    setAssignDraft('');
  }

  const archiveLead = bulkApi.archiveLead;
  const restoreLead = bulkApi.restoreLead;

  function confirmDelete() {
    if (!archiveLead) return;
    confirm({
      title: CRM_COPY.bulk.deleteConfirmTitle,
      // Says the delete is undoable in as many words: the button reads
      // permanent, the endpoint is a soft archive, and the leads land on the
      // Deleted tab where the per-row restore already lives.
      content: `${CRM_COPY.bulk.deleteConfirmPrefix} ${selectedIds.length} ${leadWord(selectedIds.length)}. ${CRM_COPY.bulk.deleteConfirmSuffix}`,
      okText: CRM_COPY.bulk.deleteConfirmOk,
      danger: true,
      onConfirm: () => run(CRM_COPY.bulk.deletedVerb, archiveLead),
    });
  }

  if (selectedIds.length === 0) return null;

  const onDeletedTab = tab === 'deleted';

  const tagPanel = (
    <Space direction="vertical" size={space.sm} style={{ width: 260 }}>
      <Radio.Group
        size="small"
        optionType="button"
        value={tagMode}
        onChange={(e) => setTagMode(e.target.value as 'add' | 'remove')}
        options={[
          { label: CRM_COPY.bulk.tagAdd, value: 'add' },
          { label: CRM_COPY.bulk.tagRemove, value: 'remove' },
        ]}
      />
      <Select
        aria-label={CRM_COPY.bulk.tagPlaceholder}
        placeholder={CRM_COPY.bulk.tagPlaceholder}
        value={tagKey ?? undefined}
        onChange={(v: string) => setTagKey(v)}
        loading={tagDefsQuery.isLoading}
        style={{ width: '100%' }}
        options={catalog.map((o) => ({ value: o.key, label: o.label }))}
      />
      <Button type="primary" size="small" block disabled={!tagKey || busy} onClick={applyTag}>
        {CRM_COPY.bulk.applyButton}
      </Button>
    </Space>
  );

  const assignPanel = (
    <Space direction="vertical" size={space.sm} style={{ width: 260 }}>
      {suggestions && suggestions.length > 0 ? (
        <AutoComplete
          value={assignDraft}
          options={suggestions
            .filter(
              (s) =>
                assignDraft.trim() === '' ||
                s.toLowerCase().includes(assignDraft.trim().toLowerCase()),
            )
            .map((s) => ({ value: s }))}
          onChange={(v: string) => setAssignDraft(v)}
          onSelect={(v: string) => setAssignDraft(v)}
          // jsdom/rc-virtual-list workaround: the 0x0 a11y mirror duplicates
          // each option under virtualization. Production renders the whole
          // (small) filtered roster instead of a window — harmless at roster
          // sizes.
          virtual={false}
          style={{ width: '100%' }}
        >
          <Input
            aria-label={CRM_COPY.assignSelectedTo}
            placeholder={CRM_COPY.bulk.assignPlaceholder}
            // Deliberately no onPressEnter here. Enter commits an
            // ARROW-highlighted option into the draft (combobox mode
            // highlights nothing by default, so a plain type+Enter only
            // toggles the dropdown); Apply is the only thing that writes — a
            // bulk write to N leads deserves the click, and a deferred
            // Enter-apply would race rc-select's commit with a stale draft.
            maxLength={200}
          />
        </AutoComplete>
      ) : (
        <Input
          aria-label={CRM_COPY.assignSelectedTo}
          placeholder={CRM_COPY.bulk.assignPlaceholder}
          value={assignDraft}
          onChange={(e) => setAssignDraft(e.target.value)}
          onPressEnter={applyAssignment}
          maxLength={200}
        />
      )}
      <Typography.Text type="secondary">{CRM_COPY.bulk.assignClearNote}</Typography.Text>
      <Button type="primary" size="small" block disabled={busy} onClick={applyAssignment}>
        {CRM_COPY.bulk.applyButton}
      </Button>
    </Space>
  );

  return (
    <div
      role="group"
      aria-label="Selected lead actions"
      style={{
        margin: `0 ${space.lg}px ${space.md}px`,
        padding: `${space.sm}px ${space.md}px`,
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        display: 'flex',
        alignItems: 'center',
        gap: space.sm,
        flexWrap: 'wrap',
      }}
    >
      <Typography.Text strong>
        {progress
          ? `${progress.done} ${CRM_COPY.bulk.resultOf} ${progress.total} ${leadWord(progress.total)} ${CRM_COPY.bulk.progressSuffix}`
          : `${selectedIds.length} ${CRM_COPY.bulk.selectedSuffix}`}
      </Typography.Text>
      {progress && progress.total > SERVER_WRITE_BURST && (
        <Typography.Text type="secondary">{CRM_COPY.bulk.pacedNote}</Typography.Text>
      )}

      {!onDeletedTab && (
        <>
          <Popover
            trigger="click"
            placement="bottomLeft"
            open={openPanel === 'tag'}
            onOpenChange={(open) => setOpenPanel(open ? 'tag' : null)}
            content={tagPanel}
          >
            <Button size="small" disabled={busy}>
              {CRM_COPY.bulk.tagButton}
            </Button>
          </Popover>
          <Popover
            trigger="click"
            placement="bottomLeft"
            open={openPanel === 'assign'}
            onOpenChange={(open) => setOpenPanel(open ? 'assign' : null)}
            content={assignPanel}
          >
            <Button size="small" disabled={busy}>
              {CRM_COPY.assignSelectedTo}
            </Button>
          </Popover>
          {onMove && (
            <Button size="small" disabled={busy} onClick={onMove}>
              Move to another event
            </Button>
          )}
        </>
      )}

      {!onDeletedTab && archiveLead && (
        <Button size="small" danger disabled={busy} onClick={confirmDelete}>
          {CRM_COPY.bulk.deleteButton}
        </Button>
      )}

      {onDeletedTab && restoreLead && (
        <Button
          size="small"
          disabled={busy}
          onClick={() => void run(CRM_COPY.bulk.restoredVerb, restoreLead)}
        >
          {CRM_COPY.bulk.restoreButton}
        </Button>
      )}

      <Button size="small" type="text" disabled={busy} onClick={onClearSelection}>
        {CRM_COPY.bulk.clearButton}
      </Button>
    </div>
  );
}
